import React, { useRef, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

interface FashionStudioHeroProps {
  onHeightChange?: (height: number) => void
}

export const FashionStudioHero = ({
  onHeightChange,
}: FashionStudioHeroProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const fashionImages = Array.from(
    { length: isMobile ? 24 : 25 },
    (_, index) => `fashion-bg-${index + 1}.webp`,
  )

  const baseStorageUrl = 'https://cdn.fastseller.shop/common'

  // 개별 이미지를 슬라이드로 사용

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 4000,
    slidesToShow: 9,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    cssEase: 'ease-in-out',
    arrows: false,
    fade: false,
    pauseOnHover: false,
    swipeToSlide: false,
    touchMove: false,
    draggable: false,
    responsive: [
      {
        breakpoint: 1536, // 2xl
        settings: {
          slidesToShow: 10,
        },
      },
      {
        breakpoint: 1280, // xl
        settings: {
          slidesToShow: 10,
        },
      },
      {
        breakpoint: 1024, // lg
        settings: {
          slidesToShow: 8,
        },
      },
      {
        breakpoint: 768, // md
        settings: {
          slidesToShow: 6,
        },
      },
      {
        breakpoint: 640, // sm
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 480, // xs
        settings: {
          slidesToShow: 3,
        },
      },
    ],
  }

  useEffect(() => {
    if (containerRef.current && onHeightChange) {
      const height = containerRef.current.offsetHeight
      onHeightChange(height)
    }
  }, [onHeightChange])

  return (
    <div className="flex flex-col" ref={containerRef}>
      <div className="relative w-screen">
        <Slider {...sliderSettings}>
          {fashionImages.map((imageName, index) => (
            <div
              key={index}
              className="aspect-[256/455] flex-1 overflow-hidden"
            >
              <picture>
                <source
                  srcSet={`${baseStorageUrl}/${imageName}`}
                  type="image/webp"
                />
                <img
                  src={`${baseStorageUrl}/${imageName.replace('.webp', '.png')}`}
                  alt={`Fashion model ${index + 1}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </picture>
            </div>
          ))}
        </Slider>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex w-full flex-col gap-4 px-4 md:max-w-[816px]">
            <p
              className="text-center leading-[1.2]"
              style={{
                fontSize: isMobile ? '45px' : '120px',
                fontFamily:
                  'Ubuntu, -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif',
                textShadow: '0px 0px 20px rgba(0, 0, 0, 1)',
                width: isMobile ? '100%' : '816px',
              }}
            >
              <span className="font-bold" style={{ color: '#FFFFFF' }}>
                Style
              </span>
              <span className="font-[300]" style={{ color: '#FFFFFF' }}>
                Room
              </span>
            </p>
          </div>
        </div>
      </div>
      {/* AI 패션 콘텐츠 가이드 섹션 */}
      <div className="mx-auto w-full max-w-[816px] px-4 py-16">
        {/* 상단 헤더 */}
        <div className="mb-8 text-center">
          <h2 className="mb-1 text-[32px] font-bold leading-[1.2] text-[#2E343D]">
            옷만 골라 오세요
          </h2>
          <h2 className="mb-2 flex flex-col items-center justify-center gap-1 text-[32px] font-bold leading-[1.4] text-[#2E343D] md:flex-row">
            <div className="flex items-center justify-center gap-2">
              <img
                src="/assets/img/logo-icon-2.png"
                alt="FastCut Logo 2"
                className="h-[63px] w-[63px] rounded-md object-cover shadow-inner md:h-[30px] md:w-[30px]"
              />
              <img
                src="/assets/img/logo-icon-1.png"
                alt="FastCut Logo 1"
                className="h-[63px] w-[63px] rounded-md object-cover md:h-[30px] md:w-[30px]"
              />
            </div>
            감성 착샷 100장,{isMobile && <br />} 스타일룸 AI가 1분 만에!
          </h2>

          <p className="mb-2 text-[24px] font-bold text-[#00B7B9]">
            Branded AI Fashion Content
          </p>
          <p className="text-[14px] text-[#5E6068] md:text-[16px]">
            from Product pages to lookbooks, start creating now
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <div className="flex flex-col justify-center gap-4 rounded-[20px] bg-[#F6F8FB] px-1 py-5 md:p-5">
            <div className="mx-auto flex h-[120px] w-[158px] flex-col items-center justify-center gap-2 rounded-[10px] bg-white">
              <img
                src="https://cdn.fastseller.shop/uploads/styleroom1.jpg"
                alt="옷 분위기에 맞는 장소 테마 선택"
                className="h-full w-full rounded-[10px] object-cover"
              />
            </div>
            <div className="text-center md:text-left">
              <p className="mb-1 text-[14px] font-bold text-[#00B7B9]">
                STEP 1
              </p>
              <h3 className="text-base font-bold leading-[1.2] text-[#2E343D] md:text-[18px]">
                옷 분위기에 딱 맞는
                <br />
                장소 · 테마 선택
              </h3>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-[20px] bg-[#F6F8FB] px-1 py-5 md:p-5">
            <div className="mx-auto flex h-[120px] w-[158px] flex-col items-center justify-center rounded-[10px] bg-white">
              <img
                src="https://cdn.fastseller.shop/uploads/styleroom2.jpg"
                alt="브랜드를 빛내줄 AI 모델 선택"
                className="h-full w-full rounded-[10px] object-cover"
              />
            </div>
            <div className="text-center md:text-left">
              <p className="mb-1 text-[14px] font-bold text-[#00B7B9]">
                STEP 2
              </p>
              <h3 className="text-base font-bold leading-[1.2] text-[#2E343D] md:text-[18px]">
                브랜드를 빛내줄
                <br />
                AI 모델 선택
              </h3>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-[20px] bg-[#F6F8FB] px-1 py-5 md:p-5">
            <div className="mx-auto flex h-[120px] w-[158px] flex-col items-center justify-center rounded-[10px] bg-white">
              <img
                src="https://cdn.fastseller.shop/uploads/styleroom3.jpg"
                alt="옷 사진 넣고 모델에게 입히기"
                className="h-full w-full rounded-[10px] object-cover"
              />
            </div>
            <div className="text-center md:text-left">
              <p className="mb-1 text-[14px] font-bold text-[#00B7B9]">
                STEP 3
              </p>
              <h3 className="text-base font-bold leading-[1.2] text-[#2E343D] md:text-[18px]">
                옷 사진 넣고,
                <br />
                모델에게 입히기
              </h3>
            </div>
          </div>

          <div className="1 flex flex-col gap-4 rounded-[20px] bg-[#F6F8FB] px-1 py-5 md:p-5">
            <div className="mx-auto flex h-[120px] w-[158px] items-center justify-center gap-2 rounded-[10px] p-2">
              <video
                className="h-full w-[68px] rounded object-cover"
                autoPlay
                muted
                loop
                playsInline
              >
                <source
                  src="https://cdn.fastseller.shop/uploads/sample1.mp4"
                  type="video/mp4"
                />
              </video>
              <video
                className="h-full w-[68px] rounded object-cover"
                autoPlay
                muted
                loop
                playsInline
              >
                <source
                  src="https://cdn.fastseller.shop/uploads/sample2.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
            <div className="text-center md:text-left">
              <p className="mb-1 text-[14px] font-bold text-[#00B7B9]">
                STEP 4
              </p>
              <h3 className="text-base font-bold leading-[1.2] text-[#2E343D] md:text-[18px]">
                착샷 이미지로
                <br />
                숏폼 클립도 생성
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
